import type { LooperEngine } from "../LooperEngine";
import type { Cell, ArrItem } from "../types";
import type { Palette } from "../../shared/palette";
import { encodeWav } from "../../engine/wavExport";
import { spawnNoteVoice } from "../../engine/spawnVoice";
import { registerDropZone, type DragData } from "../../shared/dragDrop";

// Arrangement view: a wide, drag-panable canvas under the loop boxes.
// Boxes and palette tiles are dropped onto it as chips. A small Play button
// schedules every chip in left-to-right order against the Web Audio clock.

const CANVAS_W = 6400;
const CANVAS_H = 2000;
const ORIGIN_X = CANVAS_W / 2;
const ORIGIN_Y = CANVAS_H / 2;
const BEAT_PX = 32;
const BAR_PX = BEAT_PX * 4;    // 4/4 bar — "add to song" lands on the next bar line
const ROW_H = 50;              // lane height — chips snap to lanes (2000/50 rows)
const PALETTE_CHIP_W = 80;
const RESIZE_W = 16;           // right-edge grab zone — wide enough for a thumb

// Chips snap to the beat grid (x) and lanes (y). `item.x/y` is the chip's
// TOP-LEFT corner; x is the musical start (1 beat = 32 px).
const snap = (v: number, step: number): number => Math.round(v / step) * step;

/** `ArrItem` + the repeat count. Optional and additive: a project saved before
 *  the field existed simply has no `repeats` and reads as 1. The field belongs
 *  on `ArrItem` in `looper/types.ts`; this local widening keeps the view
 *  working either way, and every read goes through `repeatsOf()`. */
type ArrChip = ArrItem & { repeats?: number };

export interface ArrangementOpts {
  engine: LooperEngine;
  palette: Palette;
  onChange: () => void;
  /** Fired whenever playback starts or stops — including the automatic stop at
   *  the end of the timeline — so an outside button can keep its label right. */
  onPlayStateChange?: (playing: boolean) => void;
}

export class ArrangementView {
  private items: ArrChip[] = [];
  private innerEl: HTMLElement;
  private playheadEl: HTMLElement;
  private offsetX = 0;
  private offsetY = 0;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private edgeLeft!: HTMLElement;
  private edgeRight!: HTMLElement;
  private edgeTop!: HTMLElement;
  private edgeBottom!: HTMLElement;
  private playing = false;
  private playStartTime = 0;     // ctx.currentTime when sweep started
  private playStartX = 0;        // chip-x of the leftmost chip
  private playRaf = 0;
  // Every voice of a playback run is scheduled into this bus, so Stop can kill
  // the whole run in one move (spawnNoteVoice hands back no per-voice handle).
  private bus: GainNode | null = null;
  private busTail: AudioNode | null = null;
  // Live drop preview — a ghost chip at the exact snapped landing spot.
  private ghostEl: HTMLElement | null = null;

  constructor(private container: HTMLElement, private opts: ArrangementOpts) {
    container.classList.add("arrangement");
    container.innerHTML = `
      <div class="arr-inner"></div>
      <div class="arr-playhead"></div>
      <div class="arr-edge arr-edge-left"></div>
      <div class="arr-edge arr-edge-right"></div>
      <div class="arr-edge arr-edge-top"></div>
      <div class="arr-edge arr-edge-bottom"></div>
    `;
    this.innerEl = container.querySelector(".arr-inner")!;
    this.playheadEl = container.querySelector(".arr-playhead")!;
    this.innerEl.style.width = `${CANVAS_W}px`;
    this.innerEl.style.height = `${CANVAS_H}px`;
    this.edgeLeft = container.querySelector(".arr-edge-left")!;
    this.edgeRight = container.querySelector(".arr-edge-right")!;
    this.edgeTop = container.querySelector(".arr-edge-top")!;
    this.edgeBottom = container.querySelector(".arr-edge-bottom")!;
    this.drawAxes();
    // Load any persisted items.
    this.items = (this.opts.engine.currentProject.arrangement ?? []).slice();
    this.refresh();
    // Start centred on the origin.
    requestAnimationFrame(() => {
      const r = this.container.getBoundingClientRect();
      this.offsetX = ORIGIN_X - r.width / 2;
      this.offsetY = ORIGIN_Y - r.height / 2;
      this.applyOffset();
    });
    this.setupPan();
    this.setupDrop();
    window.addEventListener("resize", () => this.applyOffset());
  }

  private clampOffset(): void {
    const r = this.container.getBoundingClientRect();
    this.offsetX = Math.max(0, Math.min(CANVAS_W - r.width, this.offsetX));
    this.offsetY = Math.max(0, Math.min(CANVAS_H - r.height, this.offsetY));
  }

  private applyOffset(): void {
    this.clampOffset();
    this.innerEl.style.transform = `translate(${-this.offsetX}px, ${-this.offsetY}px)`;
    this.updateEdgeMarkers();
    this.updatePlayhead();
  }

  private updateEdgeMarkers(): void {
    const r = this.container.getBoundingClientRect();
    const xVisible = this.offsetX <= ORIGIN_X && ORIGIN_X <= this.offsetX + r.width;
    const yVisible = this.offsetY <= ORIGIN_Y && ORIGIN_Y <= this.offsetY + r.height;
    this.edgeLeft.style.opacity   = !xVisible && ORIGIN_X < this.offsetX ? "1" : "0";
    this.edgeRight.style.opacity  = !xVisible && ORIGIN_X > this.offsetX + r.width ? "1" : "0";
    this.edgeTop.style.opacity    = !yVisible && ORIGIN_Y < this.offsetY ? "1" : "0";
    this.edgeBottom.style.opacity = !yVisible && ORIGIN_Y > this.offsetY + r.height ? "1" : "0";
  }

  private setupPan(): void {
    this.container.addEventListener("pointerdown", (e) => {
      const t = e.target as HTMLElement;
      if (t.closest(".arr-chip")) return;
      this.container.setPointerCapture(e.pointerId);
      this.dragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
    });
    this.container.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      this.offsetX -= e.clientX - this.lastX;
      this.offsetY -= e.clientY - this.lastY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      this.applyOffset();
    });
    const end = (): void => { this.dragging = false; };
    this.container.addEventListener("pointerup", end);
    this.container.addEventListener("pointercancel", end);
  }

  private setupDrop(): void {
    // Pointer-based drop zone (works on touch). Palette tiles and box grips are
    // dragged here and dropped as chips. The chip's LEFT EDGE lands on the
    // pointer (it is the musical start time), and a ghost previews the exact
    // snapped spot while you hover — dropping used to be pure guesswork.
    registerDropZone({
      el: this.container,
      overClass: "drop",
      accepts: () => true,
      onDragOver: (d, clientX, clientY) => this.showGhost(d, clientX, clientY),
      onDragLeave: () => this.hideGhost(),
      onDrop: (d, clientX, clientY) => {
        this.hideGhost();
        const width = this.dragWidth(d);
        const pos = this.dropPos(clientX, clientY, width);
        if (d.kind === "slot") {
          const slot = this.opts.palette[Number(d.id)];
          if (slot) this.addItem({ ...pos, width, source: "palette", sourceId: slot.id });
        } else {
          const cell = this.opts.engine.currentProject.cells.find((c) => c.id === d.id);
          if (cell) this.addItem({ ...pos, width, source: "cell", sourceId: cell.id });
        }
      },
    });
  }

  /** How wide the chip for this drag will be — the ghost and the drop agree. */
  private dragWidth(d: DragData): number {
    if (d.kind === "slot") return PALETTE_CHIP_W;
    const cell = this.opts.engine.currentProject.cells.find((c) => c.id === d.id);
    return cell ? cell.lengthBeats * BEAT_PX : PALETTE_CHIP_W;
  }

  /** Keep a chip fully on the canvas (a drop near the top/left used to produce
   *  negative coordinates and an unreachable chip). */
  private clampPos(x: number, y: number, width: number): { x: number; y: number } {
    return {
      x: Math.max(0, Math.min(CANVAS_W - width, x)),
      y: Math.max(0, Math.min(CANVAS_H - ROW_H, y)),
    };
  }

  /** Screen point → the snapped, clamped top-left the chip will occupy. */
  private dropPos(clientX: number, clientY: number, width: number): { x: number; y: number } {
    const rect = this.container.getBoundingClientRect();
    const innerX = clientX - rect.left + this.offsetX;
    const innerY = clientY - rect.top + this.offsetY;
    // x = the chip's start time, so its left edge snaps to the pointer's beat.
    // y = the lane the pointer is inside.
    return this.clampPos(snap(innerX, BEAT_PX), snap(innerY - ROW_H / 2, ROW_H), width);
  }

  private showGhost(d: DragData, clientX: number, clientY: number): void {
    const width = this.dragWidth(d);
    const pos = this.dropPos(clientX, clientY, width);
    if (!this.ghostEl) {
      const g = document.createElement("div");
      g.className = "arr-ghost";
      g.style.position = "absolute";
      g.style.pointerEvents = "none";
      this.innerEl.append(g);
      this.ghostEl = g;
    }
    this.ghostEl.style.left = `${pos.x}px`;
    this.ghostEl.style.top = `${pos.y}px`;
    this.ghostEl.style.width = `${width}px`;
    this.ghostEl.style.height = `${ROW_H}px`;
  }

  private hideGhost(): void {
    this.ghostEl?.remove();
    this.ghostEl = null;
  }

  /** Whole repeats of a chip. Tolerates the field being absent (old projects),
   *  fractional or junk — a chip can never play fewer than one pass. */
  private repeatsOf(item: ArrChip): number {
    const n = Math.round(item.repeats ?? 1);
    return Number.isFinite(n) ? Math.max(1, n) : 1;
  }

  /** Width of ONE pass in px — the chip's length before repeats. For a box it
   *  comes from the live cell (so an edited lengthBeats still rules); for a
   *  palette chip (and an orphan) it is derived back out of the stored width. */
  private unitWidth(item: ArrChip): number {
    if (item.source === "cell") {
      const cell = this.opts.engine.currentProject.cells.find((c) => c.id === item.sourceId);
      if (cell) return cell.lengthBeats * BEAT_PX;
    }
    return Math.max(BEAT_PX, Math.round(item.width / this.repeatsOf(item)));
  }

  private addItem(base: Omit<ArrChip, "id">): ArrChip {
    const item: ArrChip = {
      id: `arr-${Date.now()}-${Math.floor(Math.random() * 1e4)}`,
      ...base,
    };
    this.items.push(item);
    this.renderItem(item);
    this.persist();
    return item;
  }

  private letterFor(cell: Cell): string {
    const idx = this.opts.engine.currentProject.cells.indexOf(cell);
    return idx >= 0 ? String.fromCharCode(65 + (idx % 26)) : "?";
  }

  // Labels are derived on every render (never snapshotted), so refresh() picks
  // up renamed sounds, re-lettered boxes and changed lengths. A chip whose
  // source is gone says so instead of showing a stale letter.
  private labelFor(item: ArrChip): { text: string; title: string; orphan: boolean } {
    if (item.source === "cell") {
      const cell = this.opts.engine.currentProject.cells.find((c) => c.id === item.sourceId);
      if (!cell) {
        return { text: "⚠ gone", title: "The box this chip came from was deleted", orphan: true };
      }
      const letter = this.letterFor(cell);
      const bpm = this.opts.engine.currentProject.bpm;
      const seconds = cell.lengthBeats * (60 / bpm);
      return {
        text: `${letter} · ${cell.lengthBeats}b`,
        title: `${letter} — ${cell.lengthBeats} beats / ${seconds.toFixed(2)}s @ ${bpm} BPM`,
        orphan: false,
      };
    }
    const slot = this.opts.palette.find((s) => s?.id === item.sourceId);
    if (!slot) {
      return { text: "⚠ gone", title: "The sound this chip came from is gone", orphan: true };
    }
    return { text: slot.name.slice(0, 12), title: slot.name, orphan: false };
  }

  // A functional chip: flat card showing the letter/name (+ mini note bars for
  // boxes). Drag = move (snaps to beats + lanes) · tap = preview · ✕ = remove ·
  // drag the right edge = repeat the clip.
  private renderItem(item: ArrChip): void {
    const chip = document.createElement("div");
    chip.className = `arr-chip arr-chip-${item.source}`;
    chip.style.left = `${item.x}px`;
    chip.style.top = `${item.y}px`;
    chip.style.height = `${ROW_H}px`;
    chip.dataset.id = item.id;

    const label = document.createElement("span");
    label.className = "arr-label";
    chip.append(label);
    // Width, label text, tooltip and note bars all follow the repeat count, so
    // they are painted in one place — render and resize share it.
    this.paintChip(chip, item);

    const del = document.createElement("button");
    del.className = "arr-x";
    del.type = "button";
    del.textContent = "✕";
    del.title = "Remove from arrangement";
    del.addEventListener("pointerdown", (e) => e.stopPropagation());
    del.addEventListener("click", (e) => {
      e.stopPropagation();
      this.items = this.items.filter((it) => it.id !== item.id);
      chip.remove();
      this.persist();
    });
    chip.append(del);

    this.addResizeHandle(chip, item);

    // Drag to reposition (snapped); a still tap previews the chip's sound.
    let moved = false;
    let px = 0, py = 0, ox = 0, oy = 0;
    chip.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest(".arr-x, .arr-resize")) return;
      e.preventDefault();
      e.stopPropagation(); // don't pan the canvas
      chip.setPointerCapture(e.pointerId);
      moved = false;
      px = e.clientX;
      py = e.clientY;
      ox = item.x;
      oy = item.y;
    });
    chip.addEventListener("pointermove", (e) => {
      if (!chip.hasPointerCapture(e.pointerId)) return;
      const dx = e.clientX - px;
      const dy = e.clientY - py;
      if (!moved && Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      moved = true;
      const pos = this.clampPos(snap(ox + dx, BEAT_PX), snap(oy + dy, ROW_H), item.width);
      item.x = pos.x;
      item.y = pos.y;
      chip.style.left = `${item.x}px`;
      chip.style.top = `${item.y}px`;
    });
    const up = (e: PointerEvent): void => {
      if (!chip.hasPointerCapture(e.pointerId)) return;
      chip.releasePointerCapture(e.pointerId);
      if (moved) this.persist();
      else if (e.type === "pointerup") this.previewItem(item);
    };
    chip.addEventListener("pointerup", up);
    chip.addEventListener("pointercancel", up);

    this.innerEl.append(chip);
  }

  /** Everything about a chip that depends on the repeat count: its width, the
   *  label (`A · 4b ×3`), the tooltip and the mini note bars. */
  private paintChip(chip: HTMLElement, item: ArrChip): void {
    const reps = this.repeatsOf(item);
    const lbl = this.labelFor(item);
    chip.style.width = `${item.width}px`;
    chip.classList.toggle("arr-chip-orphan", lbl.orphan);
    const label = chip.querySelector<HTMLElement>(".arr-label");
    if (label) label.textContent = reps > 1 ? `${lbl.text} ×${reps}` : lbl.text;
    const times = reps > 1 ? ` · ×${reps}` : "";
    chip.title = lbl.orphan
      ? `${lbl.title} — ✕ to remove it`
      : `${lbl.title}${times} — drag to move · tap to preview · drag the right edge to repeat`;
    if (item.source === "cell") this.drawChipNotes(chip, item);
  }

  // Drag the right edge to repeat the clip, DAW-style. It snaps to whole passes
  // (a half loop can't be played) and the chip's width follows, so the timeline
  // still reads as time. Its own pointer capture + stopPropagation means the
  // body's move/tap handlers never see the gesture.
  private addResizeHandle(chip: HTMLElement, item: ArrChip): void {
    const h = document.createElement("div");
    h.className = "arr-resize";
    h.title = "Drag right to repeat this clip";
    // Geometry inline (same approach as the drop ghost) so the handle is usable
    // before any CSS exists; the class only carries the look. It starts below
    // the ✕ so it can never swallow a delete tap.
    h.style.position = "absolute";
    h.style.right = "0";
    h.style.top = "18px";
    h.style.bottom = "0";
    h.style.width = `${RESIZE_W}px`;
    h.style.cursor = "ew-resize";
    h.style.touchAction = "none";
    chip.append(h);

    let px = 0, unit = 0, startReps = 1, reps = 1;
    h.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      e.stopPropagation(); // not a move, not a tap, and don't pan the canvas
      h.setPointerCapture(e.pointerId);
      px = e.clientX;
      unit = this.unitWidth(item);
      startReps = this.repeatsOf(item);
      reps = startReps;
    });
    h.addEventListener("pointermove", (e) => {
      if (!h.hasPointerCapture(e.pointerId)) return;
      // Never below 1, and never past the right edge of the canvas.
      const maxReps = Math.max(1, Math.floor((CANVAS_W - item.x) / unit));
      const want = Math.round((startReps * unit + (e.clientX - px)) / unit);
      const next = Math.min(maxReps, Math.max(1, want));
      if (next === reps) return;
      reps = next;
      this.setRepeats(item, reps);
      this.paintChip(chip, item);
    });
    const end = (e: PointerEvent): void => {
      if (!h.hasPointerCapture(e.pointerId)) return;
      h.releasePointerCapture(e.pointerId);
      if (reps !== startReps) this.persist();
    };
    h.addEventListener("pointerup", end);
    h.addEventListener("pointercancel", end);
  }

  /** Apply a repeat count and keep `width` (= the sounding extent) in step.
   *  A count of 1 drops the field entirely, so files stay as they were. */
  private setRepeats(item: ArrChip, reps: number): void {
    const n = Math.max(1, Math.round(reps));
    const unit = this.unitWidth(item);
    if (n > 1) item.repeats = n;
    else delete item.repeats;
    item.width = unit * n;
  }

  /** Hear what a chip is before/without playing the whole arrangement. */
  private previewItem(item: ArrChip): void {
    const engine = this.opts.engine;
    void engine.resume();
    if (item.source === "cell") {
      engine.playCellOnce(item.sourceId, engine.ctx.currentTime + 0.05);
      return;
    }
    const slot = this.opts.palette.find((s) => s?.id === item.sourceId);
    if (!slot) return;
    if (slot.kind === "phrase") {
      const t0 = engine.ctx.currentTime + 0.05;
      for (const ev of slot.events) engine.scheduleNote(slot.patch, ev.midi, t0 + ev.time, ev.dur);
    } else {
      engine.previewPatch(slot.patch, slot.lastMidi ?? 60);
    }
  }

  // Mini note bars inside a cell chip — the box's rhythm at a glance (same
  // mapping as the box timeline, without grid/playhead). The pattern is drawn
  // once per repeat, with a hairline where each pass restarts. Reuses the chip's
  // canvas so a live resize can repaint without rebuilding the chip.
  private drawChipNotes(chip: HTMLElement, item: ArrChip): void {
    const cell = this.opts.engine.currentProject.cells.find((c) => c.id === item.sourceId);
    let canvas = chip.querySelector<HTMLCanvasElement>(".arr-notes");
    if (!cell || cell.hits.length === 0) {
      canvas?.remove();
      return;
    }
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.className = "arr-notes";
      chip.prepend(canvas); // behind the label
    }
    canvas.width = item.width;
    canvas.height = ROW_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, item.width, ROW_H);
    const reps = this.repeatsOf(item);
    const unit = this.unitWidth(item);
    const dur = cell.lengthBeats * (60 / this.opts.engine.currentProject.bpm);
    for (let r = 1; r < reps; r++) {
      ctx.fillStyle = "rgba(233, 239, 246, 0.25)";
      ctx.fillRect(r * unit, 0, 1, ROW_H);
    }
    ctx.fillStyle = "rgba(233, 239, 246, 0.7)";
    for (let r = 0; r < reps; r++) {
      const x0 = r * unit;
      for (const hit of cell.hits) {
        const x = x0 + (hit.time / dur) * unit;
        const bw = Math.max(2, (hit.dur / dur) * unit);
        const pitch = Math.min(1, Math.max(0, (hit.midi - 36) / 60));
        const y = (1 - pitch) * (ROW_H - 12) + 6;
        ctx.fillRect(x, y - 1.5, Math.min(bw, item.width - x), 3);
      }
    }
  }

  private drawAxes(): void {
    const cross = document.createElement("div");
    cross.className = "arr-origin";
    cross.style.left = `${ORIGIN_X}px`;
    cross.style.top = `${ORIGIN_Y}px`;
    this.innerEl.append(cross);

    const vline = document.createElement("div");
    vline.className = "arr-axis arr-axis-v";
    vline.style.left = `${ORIGIN_X}px`;
    this.innerEl.append(vline);

    const hline = document.createElement("div");
    hline.className = "arr-axis arr-axis-h";
    hline.style.top = `${ORIGIN_Y}px`;
    this.innerEl.append(hline);
  }

  private persist(): void {
    this.opts.engine.currentProject.arrangement = this.items.slice();
    this.opts.onChange();
  }

  // ---- Public API for the page shell ----

  /** Re-render every chip from the *current* cell/palette state. Call after any
   *  edit that can change a chip: deleting a box (later boxes re-letter), a new
   *  lengthBeats (the chip resizes), edited notes, a renamed sound. */
  refresh(): void {
    for (const el of Array.from(this.innerEl.querySelectorAll(".arr-chip"))) el.remove();
    for (const item of this.items) {
      if (item.source === "cell") {
        const cell = this.opts.engine.currentProject.cells.find((c) => c.id === item.sourceId);
        // Width is the *sounding* extent, so it follows the repeat count too.
        if (cell) item.width = cell.lengthBeats * BEAT_PX * this.repeatsOf(item);
      }
      this.renderItem(item);
    }
  }

  /** Rebuild the arrangement from the engine's current project — call it right
   *  after loading a project file, otherwise the old chips are still in memory
   *  and overwrite `project.arrangement` on the next persist. */
  reload(): void {
    this.stop();
    this.hideGhost();
    this.items = (this.opts.engine.currentProject.arrangement ?? []).slice();
    this.refresh();
  }

  /** The no-drag path: append a box to the end of the song — on the next bar
   *  line after the rightmost chip, in that chip's lane — and scroll to it. */
  appendCell(cellId: string): void {
    const cell = this.opts.engine.currentProject.cells.find((c) => c.id === cellId);
    if (!cell) return;
    const width = cell.lengthBeats * BEAT_PX;
    let x = snap(ORIGIN_X, BAR_PX);
    let y = snap(ORIGIN_Y - ROW_H / 2, ROW_H);
    if (this.items.length > 0) {
      // `width` already spans every repeat, so the end of the song is the end of
      // the last chip's LAST pass — not its first.
      const last = this.items.reduce((a, b) => (b.x + b.width > a.x + a.width ? b : a));
      x = Math.ceil((last.x + last.width) / BAR_PX) * BAR_PX;
      y = last.y;
    }
    const item = this.addItem({ ...this.clampPos(x, y, width), width, source: "cell", sourceId: cell.id });
    this.reveal(item);
  }

  /** Pan the canvas so a chip is centred in view. */
  private reveal(item: ArrChip): void {
    const r = this.container.getBoundingClientRect();
    this.offsetX = item.x + item.width / 2 - r.width / 2;
    this.offsetY = item.y + ROW_H / 2 - r.height / 2;
    this.applyOffset();
  }

  // ---- Playback ----

  // The one place chips become scheduled notes. Live playback and the offline
  // WAV render both go through it, so an export always matches what you heard.
  private scheduleItems(ctx: BaseAudioContext, master: AudioNode, t0: number): void {
    if (this.items.length === 0) return;
    const sorted = this.items.slice().sort((a, b) => a.x - b.x);
    const leftmost = sorted[0].x;
    const pxPerSec = BEAT_PX * (this.opts.engine.currentProject.bpm / 60);
    const palette = this.opts.engine.currentProject.palette;
    const findPatch = (id: string) => palette.find((p) => p.id === id)?.patch;

    for (const item of sorted) {
      const when = t0 + (item.x - leftmost) / pxPerSec;
      // A chip can repeat: pass r starts one clip-length after pass r-1. The
      // step is the chip's own drawn length, so what you see on the ruler is
      // exactly what is scheduled — live and in the offline render alike.
      const reps = this.repeatsOf(item);
      const step = this.unitWidth(item) / pxPerSec;
      if (item.source === "cell") {
        const cell = this.opts.engine.currentProject.cells.find((c) => c.id === item.sourceId);
        if (!cell || cell.muted) continue;
        const dest = new GainNode(ctx, { gain: cell.gain });
        dest.connect(master);
        for (let r = 0; r < reps; r++) {
          const at = when + r * step;
          for (const hit of cell.hits) {
            const patch = findPatch(hit.patchId);
            if (!patch) continue;
            spawnNoteVoice(
              ctx, patch, dest,
              at + hit.time / cell.speed,
              Math.max(0.05, hit.dur) / cell.speed,
              hit.midi + cell.transpose,
              hit.gain ?? 1,
            );
          }
        }
      } else {
        const slot = this.opts.palette.find((s) => s?.id === item.sourceId);
        if (!slot) continue;
        for (let r = 0; r < reps; r++) {
          const at = when + r * step;
          if (slot.kind === "phrase") {
            for (const ev of slot.events) {
              spawnNoteVoice(ctx, slot.patch, master, at + ev.time, Math.max(0.05, ev.dur), ev.midi);
            }
          } else {
            spawnNoteVoice(ctx, slot.patch, master, at, 0.5, slot.lastMidi ?? 60);
          }
        }
      }
    }
  }

  // A dedicated bus for one playback run. spawnNoteVoice returns no handle, so
  // this node IS the handle: unhooking it silences everything the run queued,
  // however far ahead it was scheduled.
  //
  // It feeds the engine's *master node*, not the analyser. That matters twice:
  // the master fader keeps working on an already-playing arrangement (it used
  // to snapshot `masterValue` at play time and then ignore you), and the
  // engine's own compressor — which already honours the FX toggle — applies
  // exactly once instead of being duplicated here.
  private openBus(): GainNode {
    this.closeBus();
    const engine = this.opts.engine;
    const bus = new GainNode(engine.ctx, { gain: 1 });
    bus.connect(engine.masterNode);
    this.busTail = null;
    this.bus = bus;
    return bus;
  }

  private closeBus(): void {
    const bus = this.bus;
    if (!bus) return;
    const tail = this.busTail;
    this.bus = null;
    this.busTail = null;
    // 20 ms fade so Stop doesn't click, then unhook the whole run.
    const now = this.opts.engine.ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(bus.gain.value, now);
    bus.gain.linearRampToValueAtTime(0, now + 0.02);
    setTimeout(() => { bus.disconnect(); tail?.disconnect(); }, 80);
  }

  // Public so a button outside the panel can drive it.
  play(): void {
    if (this.playing) { this.stop(); return; }
    if (this.items.length === 0) return;
    const engine = this.opts.engine;
    void engine.resume();
    const t0 = engine.ctx.currentTime + 0.1;
    this.scheduleItems(engine.ctx, this.openBus(), t0);
    this.playing = true;
    this.playStartTime = t0;
    this.playStartX = Math.min(...this.items.map((i) => i.x));
    this.playRaf = requestAnimationFrame(this.tickPlayhead);
    this.opts.onPlayStateChange?.(true);
  }

  stop(): void {
    const was = this.playing;
    this.playing = false;
    cancelAnimationFrame(this.playRaf);
    this.playheadEl.style.opacity = "0";
    this.closeBus();
    if (was) this.opts.onPlayStateChange?.(false);
  }

  isPlaying(): boolean { return this.playing; }

  private tickPlayhead = (): void => {
    if (!this.playing) return;
    const ctx = this.opts.engine.ctx;
    const pxPerSec = BEAT_PX * (this.opts.engine.currentProject.bpm / 60);
    const elapsed = ctx.currentTime - this.playStartTime;
    const chipX = this.playStartX + elapsed * pxPerSec;
    // Stop once past the last chip + 1s.
    const last = this.items.length === 0 ? 0 : Math.max(...this.items.map((i) => i.x + i.width));
    if (elapsed > 0 && chipX > last + pxPerSec) {
      this.stop();
      return;
    }
    this.updatePlayhead(chipX);
    this.playRaf = requestAnimationFrame(this.tickPlayhead);
  };

  // ---- WAV export ----
  // Renders the arrangement to PCM offline (faster than realtime), respects the
  // current engine.fxEnabled toggle, returns a downloadable .wav blob.
  async exportWav(): Promise<Blob | null> {
    if (this.items.length === 0) return null;
    const leftmost = Math.min(...this.items.map((i) => i.x));
    const rightmost = Math.max(...this.items.map((i) => i.x + i.width));
    const bpm = this.opts.engine.currentProject.bpm;
    const pxPerSec = BEAT_PX * (bpm / 60);
    const tail = 2;
    const duration = Math.max(0.5, (rightmost - leftmost) / pxPerSec) + tail;
    const sampleRate = 44100;

    const offline = new OfflineAudioContext(2, Math.ceil(sampleRate * duration), sampleRate);
    const master = new GainNode(offline, { gain: this.opts.engine.masterValue });
    // Mirror the live chain: compressor only when FX is on.
    if (this.opts.engine.fxEnabled) {
      const comp = new DynamicsCompressorNode(offline, {
        threshold: -18, knee: 8, ratio: 6, attack: 0.005, release: 0.12,
      });
      master.connect(comp);
      comp.connect(offline.destination);
    } else {
      master.connect(offline.destination);
    }

    this.scheduleItems(offline, master, 0);

    const rendered = await offline.startRendering();
    return encodeWav(rendered);
  }

  private updatePlayhead(chipX?: number): void {
    if (!this.playing) {
      this.playheadEl.style.opacity = "0";
      return;
    }
    const ctx = this.opts.engine.ctx;
    const pxPerSec = BEAT_PX * (this.opts.engine.currentProject.bpm / 60);
    const x = chipX ?? this.playStartX + (ctx.currentTime - this.playStartTime) * pxPerSec;
    const screenX = x - this.offsetX;
    this.playheadEl.style.transform = `translateX(${screenX}px)`;
    this.playheadEl.style.opacity = "1";
  }
}
